import {
    Box,
    Button,
    CardActions,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from "@mui/material"
import { grey } from "@mui/material/colors"
import { NexAutocomplete } from "../../components/Fields/Autocomplete"
import { NumberField } from "../../components/Fields/NumberField"
import {
    NexCard,
    NexCardActionButton,
    NexCardContent,
    NexCardHeader,
    NexCardTransitionGroup,
} from "../../components/NexCard"
import { Subtitle2 } from "../../components/Typography/Typography"
import { useMissions } from "../../hooks/useMissions"
import { MissionShippingInfoWithReceiptDto, MissionShippingInfoWrapper } from "../../models/missions/ShippingInfo"
import { OfficeId, officeIdOptions } from "../../store/types/MissionsTypes"
import { shippingInfoMissingFields } from "../../utils/MissionUtils"
import { useShippingInfoStore } from "../../zstore/ShippingInfoStore"
import { ShippingInfoCard } from "./ShippingInfoCard"

export const ShippingInfoCardList = () => {
    const { missions } = useMissions()
    const { isVendorFlow } = missions

    const allShippingInfo = useShippingInfoStore((state) => state.allShippingInfoWrappers)
    const setAllShippingInfo = useShippingInfoStore((state) => state.setAllShippingInfo)
    const selectedShippingWrapperToEdit = useShippingInfoStore((state) => state.selectedShippingWrapperToEdit)
    const setSelectedShippingWrapperToEdit = useShippingInfoStore((state) => state.setSelectedShippingWrapperToEdit)

    const shippingInfo = selectedShippingWrapperToEdit?.shipping_info

    const missingSelectedShippingFields = selectedShippingWrapperToEdit
        ? shippingInfoMissingFields(selectedShippingWrapperToEdit.shipping_info, isVendorFlow)
        : []

    const handleSaveChanges = () => {
        const updatedAllShipping = allShippingInfo.slice()
        if (selectedShippingWrapperToEdit) {
            const indexOfSelected = updatedAllShipping.findIndex(
                (info) => info.shipping_info.id === selectedShippingWrapperToEdit?.shipping_info.id,
            )

            if (indexOfSelected !== -1) {
                updatedAllShipping.splice(indexOfSelected, 1, selectedShippingWrapperToEdit)
            } else {
                updatedAllShipping.push(selectedShippingWrapperToEdit)
            }
            setAllShippingInfo(updatedAllShipping)
        }
        setSelectedShippingWrapperToEdit(undefined)
    }

    const handleCancelChanges = () => {
        setSelectedShippingWrapperToEdit(undefined)
    }

    const handleUpdateShippingInfo = (shippingInfo: Partial<MissionShippingInfoWithReceiptDto>) => {
        if (selectedShippingWrapperToEdit) {
            setSelectedShippingWrapperToEdit({
                ...selectedShippingWrapperToEdit,
                shipping_info: { ...selectedShippingWrapperToEdit.shipping_info, ...shippingInfo },
            })
        }
    }

    const handleAddNewShippingInfo = () => {
        const initShippingInfo: MissionShippingInfoWrapper = {
            shipping_info: { office: OfficeId.ANC, raw_storage_size_gb: null },
            fe_id: allShippingInfo.length + 1,
        }

        setSelectedShippingWrapperToEdit(initShippingInfo)
    }

    return (
        <Box>
            <NexCardTransitionGroup>
                {allShippingInfo.map((wrapper) => (
                    <Collapse key={wrapper.fe_id}>
                        <ShippingInfoCard shippingInfoWrapper={wrapper} />
                    </Collapse>
                ))}
                {/* Until we allow for multiple shipping infos per mission, we are limiting to only add 1 */}
                <Collapse>
                    {allShippingInfo.length === 0 && (
                        <NexCard>
                            <NexCardHeader backgroundColor={grey[300]} />
                            <NexCardContent>
                                <Typography variant="h6">Send Data</Typography>
                                {allShippingInfo.length === 0 && <Subtitle2 disableGutter={true}>Required</Subtitle2>}
                                <Typography>
                                    Detail data transfers (physical or digital) to track their receipt.
                                </Typography>
                            </NexCardContent>
                            <CardActions sx={{ justifyContent: "flex-end" }}>
                                <NexCardActionButton onClick={handleAddNewShippingInfo}>
                                    Add Transfer Method
                                </NexCardActionButton>
                            </CardActions>
                        </NexCard>
                    )}
                </Collapse>
            </NexCardTransitionGroup>
            <Dialog open={shippingInfo != null}>
                <DialogTitle>Shipped Drive</DialogTitle>

                {shippingInfo && (
                    <DialogContent>
                        <TextField
                            variant="outlined"
                            fullWidth={true}
                            label={"Shipped Drive Name"}
                            helperText={!isVendorFlow && "Required (deliverable drive sent to office)"}
                            value={shippingInfo.delivery_drive_name ?? ""}
                            sx={{ my: 2 }}
                            onChange={(e) => handleUpdateShippingInfo({ delivery_drive_name: e.target.value })}
                            error={!isVendorFlow && !shippingInfo.delivery_drive_name}
                        />
                        <TextField
                            variant="outlined"
                            fullWidth={true}
                            label={"Tracking Number"}
                            value={shippingInfo.shipping_number ?? ""}
                            sx={{ mb: 2 }}
                            onChange={(e) => handleUpdateShippingInfo({ shipping_number: e.target.value })}
                        />
                        <NexAutocomplete
                            shrinkLabel
                            value={shippingInfo.office ?? null}
                            options={officeIdOptions}
                            TextFieldProps={{ label: "Office Data Shipped to", required: true }}
                            fullWidth={true}
                            sx={{ mb: 2 }}
                            onChange={(_, value) => value && handleUpdateShippingInfo({ office: value })}
                        />
                        <NumberField
                            size="medium"
                            label="Raw Storage Size"
                            value={shippingInfo.raw_storage_size_gb}
                            required={!isVendorFlow}
                            endAdornment={"GB"}
                            fullWidth={true}
                            onChange={(value) => handleUpdateShippingInfo({ raw_storage_size_gb: value ?? null })}
                            error={!isVendorFlow && !shippingInfo.raw_storage_size_gb}
                        />
                    </DialogContent>
                )}
                <DialogActions sx={{ justifyContent: "flex-end", px: 2 }}>
                    <Box>
                        <Button variant="text" onClick={handleCancelChanges}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSaveChanges}
                            disabled={missingSelectedShippingFields.length !== 0}
                        >
                            Save
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
